import type { Offer } from '../types/offerTypes';

const ORDINALS = ['1ST', '2ND', '3RD', '4TH', '5TH', '6TH', '7TH', '8TH', '9TH', '10TH'];

function ordinal(rank: number): string {
  return ORDINALS[rank - 1] ?? `${rank}TH`;
}

function isSelected(offer: Offer): boolean {
  return offer.status === 'selected' || offer.isRecommended;
}

function retailerCellHtml(offer: Offer): string {
  const badge = isSelected(offer)
    ? '<span style="display:inline-block;margin-left:8px;padding:2px 8px;border-radius:9999px;background:#dcfce7;color:#15803d;font-size:11px;font-weight:600;vertical-align:middle;">SELECTED</span>'
    : '';

  if (offer.retailerLogo) {
    return `<img src="${offer.retailerLogo}" alt="${offer.retailerName}" style="height:24px;max-width:140px;object-fit:contain;vertical-align:middle;" />${badge}`;
  }
  return `<span style="font-weight:600;color:#111827;">${offer.retailerName}</span>${badge}`;
}

function resultsRowHtml(offer: Offer, rank: number): string {
  const rowBg = isSelected(offer) ? 'background:#f0fdf4;' : rank % 2 === 0 ? 'background:#fafafa;' : '';

  return `
    <tr style="${rowBg}">
      <td style="border:1px solid #111827;padding:10px 14px;font-weight:700;color:#111827;width:35%;">${ordinal(rank)}</td>
      <td style="border:1px solid #111827;padding:10px 14px;text-align:center;">${retailerCellHtml(offer)}</td>
    </tr>`;
}

function tableForOffers(siteOffers: Offer[]): string {
  const sorted = [...siteOffers].sort(
    (a, b) => (a.estimatedAnnualCost ?? Infinity) - (b.estimatedAnnualCost ?? Infinity),
  );

  return `
    <table style="width:100%;border-collapse:collapse;font-family:'Segoe UI',Arial,sans-serif;font-size:13px;margin-bottom:16px;">
      <thead>
        <tr>
          <th style="border:1px solid #111827;padding:10px 14px;text-align:left;background:#ffffff;">POSITION</th>
          <th style="border:1px solid #111827;padding:10px 14px;text-align:center;background:#ffffff;">RETAILER</th>
        </tr>
      </thead>
      <tbody>
        ${sorted.map((offer, i) => resultsRowHtml(offer, i + 1)).join('')}
      </tbody>
    </table>`;
}

/**
 * Builds a ranked position-vs-retailer table ordered by lowest estimated
 * annual cost first, with the selected/recommended offer's row highlighted.
 * Grouped by site when the tender has more than one.
 */
export function buildOfferComparisonEmailHtml(offers: Offer[]): string {
  if (offers.length === 0) return '';

  const bySite = new Map<string, Offer[]>();
  for (const offer of offers) {
    const key = offer.siteName || 'All sites';
    if (!bySite.has(key)) bySite.set(key, []);
    bySite.get(key)!.push(offer);
  }

  if (bySite.size <= 1) return tableForOffers(offers);

  return Array.from(bySite.entries())
    .map(
      ([siteName, siteOffers]) => `
        <p style="font-family:'Segoe UI',Arial,sans-serif;font-size:13px;font-weight:600;color:#374151;margin:14px 0 6px;">${siteName}</p>
        ${tableForOffers(siteOffers)}`,
    )
    .join('');
}
